"use client"

import {useCallback, useMemo, useState} from "react";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {integrationKeys, repoMetaKeys} from "@/hooks/queries/queryKeys";
import {useBoundStore} from "@/hooks/queries/stores/use-bound-store";
import {useShallow} from "zustand/react/shallow";
import {useOptionalCompanyId} from "@/hooks/use-company-id";
import {ProviderType} from "@/app/api/integrations/connect/types";
import {getKey} from "@/hooks/queries/stores/integrationsStore";
import {githubManageUrlAPI, githubPersonalOAuthStatusAPI, integrationAPI, jiraForgeAppStatusAPI} from "@/app/apiPaths";
import {GetGitHubManageUrlResponse, GetIntegrationPropertiesResponse} from "@/pb/api/devplan/services/web/company";
import {toast} from "sonner";
import {buildRepoProviderMap} from "@/app/util/gitUtil";
import {GitProvider} from "@/pb/api/devplan/types/integrations";
import type {GitHubPersonalOAuthStatusResponse} from "@/app/api/v1/company/[companyIdStr]/integration/github/personal/status/route";
import {jr} from "@/hooks/queries/response-handler";

export const useIntegrationProperties = (companyId: number, provider: ProviderType) => {
  const {integration, loading, error, reload} = useIntegration(companyId, provider)
  const properties = (integration && !integration.properties) ? null : integration?.properties
  return {properties, loading, error, reload}
}

export const useIntegrationRefresh = (companyId: number, provider: ProviderType) => {
  const {refreshIntegration} = useBoundStore()
  const [refreshing, setRefreshing] = useState(false)

  const refresh = useCallback(async () => {
    if (refreshing) return;
    setRefreshing(true)
    return refreshIntegration(companyId, provider)
      .then(() => {
        toast.success("Refreshed")
        return true
      })
      .catch(err => {
        toast.error(`Failed to refresh: ${err}`)
        return false
      })
      .finally(() => setRefreshing(false))
  }, [refreshing, refreshIntegration, companyId, provider])
  return {refresh, refreshing}
}

export const useIntegration = (companyId: number, provider: ProviderType, enabled = true) => {
  const queryFn = useCallback(async () => {
    const resp = await fetch(integrationAPI(companyId, provider))
    if (!resp.ok) {
      return Promise.reject(resp.statusText)
    }
    return GetIntegrationPropertiesResponse.fromJSON(await resp.json())
  }, [provider, companyId])
  const queryKey = integrationKeys.property(companyId, provider)

  const {data, isPending, error, refetch: reload} = useQuery({queryKey, queryFn, enabled})
  if (error) {
    console.error("Failed to load data", error)
  }
  const integration = data || undefined
  const loading = enabled && isPending
  return {integration, loading, error, reload}
}

export const useSlackDisconnect = (companyId: number) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      await fetch(integrationAPI(companyId, "slack"), {method: "DELETE"}).then(jr)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({queryKey: integrationKeys.property(companyId, "slack")})
    },
  })
}

export const useRepoMetas = (companyId: number) => {
  const {fetchRepoMetadata} = useBoundStore()
  const queryFn = useCallback(async () => fetchRepoMetadata(companyId).then(() => true), [companyId, fetchRepoMetadata])
  const queryKey = repoMetaKeys.all(companyId)
  const {isPending: loading, error, refetch: reload} = useQuery({queryKey, queryFn})
  const metas = useBoundStore(useShallow(s => s.repoMetadata[companyId]))
  return {metas, loading, error, reload}
}

export const useLinear = (companyId: number) => {
  const {integration, loading, error, reload} = useIntegration(companyId, 'linear')
  const info = integration?.info
  const linear = info?.info?.$case === "linear" ? info.info.value : undefined
  return {info: linear, loading, error, reload}
}

export const useJira = (companyId: number) => {
  const {integration, loading, error, reload} = useIntegration(companyId, 'jira')
  const info = integration?.info
  const jira = info?.info?.$case === "jira" ? info.info.value : undefined
  const jiraProperties = integration?.properties?.jira
  return {info: jira, jiraProperties, loading, error, reload}
}

export const useJiraForgeAppInstalled = (companyId: number, siteId: string | undefined) => {
  const {data, isPending: loading, refetch} = useQuery({
    queryKey: integrationKeys.jiraForgeAppStatus(companyId),
    enabled: !!siteId,
    queryFn: async () => {
      const resp = await fetch(jiraForgeAppStatusAPI(companyId));
      if (!resp.ok) {
        return Promise.reject(resp.statusText);
      }
      return resp.json() as Promise<{installed: boolean}>;
    },
  });
  return {installed: data?.installed ?? false, loading, refetch};
}

export const useNotion = (companyId: number) => {
  const {integration, loading, error, reload} = useIntegration(companyId, 'notion')
  const info = integration?.info
  const notion = info?.info?.$case === "notion" ? info.info.value : undefined
  return {info: notion, loading, error, reload}
}

export const useConfluence = (companyId: number) => {
  const {integration, loading, error, reload} = useIntegration(companyId, 'confluence')
  const info = integration?.info
  const confluence = info?.info?.$case === "confluence" ? info.info.value : undefined
  return {info: confluence, loading, error, reload}
}

export const useSlack = (companyId: number) => {
  const {integration, loading, error, reload} = useIntegration(companyId, 'slack')
  const info = integration?.info
  const slack = info?.info?.$case === "slack" ? info.info.value : undefined
  return {info: slack, loading, error, reload}
}

// Returns true iff the current workspace has a Slack integration with a
// populated workspace id. Reads the company id from the route, so it's safe
// to call from any component nested under /company/[companyId]/…; outside
// that subtree the query is skipped and `connected` stays false.
export const useSlackConnected = () => {
  const companyId = useOptionalCompanyId()
  const {data, isPending: loading} = useQuery({
    queryKey: integrationKeys.property(companyId ?? 0, 'slack'),
    enabled: companyId !== undefined,
    queryFn: async () => {
      const resp = await fetch(integrationAPI(companyId!, 'slack'))
      if (!resp.ok) return Promise.reject(resp.statusText)
      return GetIntegrationPropertiesResponse.fromJSON(await resp.json())
    },
  })
  const info = data?.info
  const slack = info?.info?.$case === "slack" ? info.info.value : undefined
  return {connected: !!slack?.workspace?.id, loading}
}

export const useAllDocumentIntegrations = (companyId: number) => {
  const {fetchLinkedDocuments} = useBoundStore()
  const queryFn = useCallback(async () => {
    await fetchLinkedDocuments(companyId)
    return companyId
  }, [companyId, fetchLinkedDocuments])
  const queryKey = integrationKeys.all(companyId)

  const {isPending: loading, error, refetch: reload} = useQuery({queryKey, queryFn})
  return {loading, error, reload}
}

export const useDocumentIntegration = (companyId: number, documentId: string, provider: ProviderType) => {
  return useBoundStore(useShallow(s => s.linkedDocuments[getKey({companyId, documentId, provider})] ?? null))
}

export const useGithubManageUrl = (companyId: number, enabled = true) => {
  const queryKey = integrationKeys.githubManageUrl(companyId)
  const queryFn = useCallback(async () => {
    const resp = await fetch(githubManageUrlAPI(companyId))
    if (!resp.ok) {
      return Promise.reject(resp.statusText)
    }
    return GetGitHubManageUrlResponse.fromJSON(await resp.json())
  }, [companyId])
  const {data, isPending: loading, error} = useQuery({queryKey, queryFn, enabled})
  return {url: data?.url ?? null, loading: enabled && loading, error}
}

export const useGithubPersonalOAuthStatus = (companyId: number, enabled = true) => {
  const queryKey = integrationKeys.githubPersonalOAuthStatus(companyId)
  const queryFn = useCallback(async () => {
    const resp = await fetch(githubPersonalOAuthStatusAPI(companyId))
    if (!resp.ok) {
      return Promise.reject(resp.statusText)
    }
    return resp.json() as Promise<GitHubPersonalOAuthStatusResponse>
  }, [companyId])
  const {data, isPending: loading, error, refetch: reload} = useQuery({queryKey, queryFn, enabled})
  return {status: data ?? {connected: false}, loading: enabled && loading, error, reload}
}

export const useRepoProviderMap = (companyId: number) => {
  const {integration: github} = useIntegration(companyId, 'github')
  const {integration: bitbucket} = useIntegration(companyId, 'bitbucket')
  const {integration: gitlab} = useIntegration(companyId, 'gitlab')
  const repoProviderMap = useMemo(
    () => buildRepoProviderMap(github?.info ?? null, bitbucket?.info ?? null, gitlab?.info ?? null),
    [github, bitbucket, gitlab],
  )
  return {repoProviderMap}
}

export const usePreferredGitProvider = (companyId: number | undefined) => {
  const githubQuery = useQuery({
    queryKey: integrationKeys.property(companyId ?? 0, 'github'),
    enabled: companyId !== undefined,
    queryFn: async () => {
      const resp = await fetch(integrationAPI(companyId!, 'github'))
      if (!resp.ok) return Promise.reject(resp.statusText)
      return GetIntegrationPropertiesResponse.fromJSON(await resp.json())
    },
  })
  const bitbucketQuery = useQuery({
    queryKey: integrationKeys.property(companyId ?? 0, 'bitbucket'),
    enabled: companyId !== undefined,
    queryFn: async () => {
      const resp = await fetch(integrationAPI(companyId!, 'bitbucket'))
      if (!resp.ok) return Promise.reject(resp.statusText)
      return GetIntegrationPropertiesResponse.fromJSON(await resp.json())
    },
  })
  const gitlabQuery = useQuery({
    queryKey: integrationKeys.property(companyId ?? 0, 'gitlab'),
    enabled: companyId !== undefined,
    queryFn: async () => {
      const resp = await fetch(integrationAPI(companyId!, 'gitlab'))
      if (!resp.ok) return Promise.reject(resp.statusText)
      return GetIntegrationPropertiesResponse.fromJSON(await resp.json())
    },
  })

  const preferredGitProvider = useMemo(() => {
    if (githubQuery.data?.info) return GitProvider.GITHUB
    if (bitbucketQuery.data?.info) return GitProvider.BIT_BUCKET
    if (gitlabQuery.data?.info) return GitProvider.GITLAB
    return undefined
  }, [githubQuery.data, bitbucketQuery.data, gitlabQuery.data])

  return {preferredGitProvider}
}
